import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import RevealText from "@/components/shared/RevealText";
import AnimatedCounter from "@/components/shared/AnimatedCounter";
import { useLang } from "@/i18n/LanguageContext";

const HERO_IMG =
  `${process.env.PUBLIC_URL}/images/hero.jpg`;

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const dim = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-black"
    >
      {/* Parallax cinematic image */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-full object-cover"
          loading="eager"
        />
      </motion.div>

      {/* Dimming */}
      <motion.div style={{ opacity: dim }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 hero-spotlight" />
      <div className="absolute inset-0 grid-lines opacity-40" />

      {/* Content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 pt-40 md:pt-44 lg:pt-52 pb-20 min-h-[100svh] flex flex-col justify-between"
      >
        {/* Top eyebrow */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="flex items-center gap-3 font-mono-eyebrow text-neutral-400"
        >
          <span className="w-8 h-px bg-[var(--dp-accent)]" />
          <span>{t.hero.eyebrow}</span>
        </motion.div>

        {/* Massive kinetic headline */}
        <div className="flex-1 flex flex-col justify-center mt-10 md:mt-0">
          <RevealText
            lines={t.hero.titleLines}
            className="font-display text-white text-[46px] sm:text-[80px] md:text-[110px] lg:text-[140px] xl:text-[164px] leading-[0.88] tracking-[-0.045em] font-medium"
            delay={0.35}
          />
          {/* Italic accent detail */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 md:mt-10 max-w-2xl text-neutral-300 text-base md:text-lg leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.div>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 1.4, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 h-[3px] w-24 md:w-40 bg-[var(--dp-accent)] origin-left"
          />
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-6 md:left-10 z-10 flex items-center gap-2 text-neutral-500 text-[11px] uppercase tracking-[0.22em]"
      >
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDown size={14} />
        </motion.span>
        {t.hero.scroll}
      </motion.div>
    </section>
  );
}
